import React, { useCallback, useMemo } from "react";
import type { DealKanbanCardProps } from "../utils/types";
import { formatDealCurrency, getDealCurrencyIcon } from "../utils/functions";
import { DEAL_LABELS } from "../utils/constants";
import CalendarIcon from "../../../../../assets/svg/calendar-icon";
import PropertyManagementIcon from "../../../../../assets/svg/property-management-icon";
import { AgentAssignmentDropdown } from "../../AgentAssignmentDropdown";
import { getDateTimeByFormat } from "../../../../../redux/slices/dateTimeFormatSlice";

const DealKanbanCard = ({
  deal,
  onSelect,
  onAssignAgent,
}: DealKanbanCardProps) => {
  const defaultSelectedAgents = useMemo(() => {
    if (!deal?.assignee?.id) return [];
    return [
      {
        id: deal.assignee.id,
        name: deal.assignee.name,
        role: "",
        status: "",
        profile_picture: null,
        email: "",
      },
    ];
  }, [deal?.assignee]);

  const CurrencyIcon = getDealCurrencyIcon(deal.currency);

  const handleScrollToggle = useCallback((lock: boolean) => {
    const containers = document.querySelectorAll(
      "[data-crm-stage-scroll-container]"
    );
    containers.forEach((el) => {
      (el as HTMLElement).style.overflowY = lock ? "hidden" : "auto";
      (el as HTMLElement).style.overflowX = "hidden";
    });
  }, []);
  return (
    <div
      onClick={() => onSelect(deal?.primary_id || deal?.contract_id || "")}
      className="group w-[300px] cursor-pointer flex flex-col gap-[8px] border border-border-gray dark:border-border-gray-dk bg-pure-white dark:bg-pure-white-dk rounded-[8px] p-[12px] hover:border-2 hover:border-crm-card-border dark:hover:border-crm-card-border-dk transition-colors ease-in-out duration-300 hover:bg-crm-card-highlight dark:hover:bg-crm-card-highlight-dk"
    >
      <div className="flex flex-col gap-[2px]">
        <p className="text-base font-bold text-verba-indigo dark:text-verba-indigo-dk">
          {deal.deal_title}
        </p>
      </div>

      <div className="flex flex-col gap-[6px]">
        <div className="flex items-center gap-[6px]">
          <CurrencyIcon className="shrink-0 w-[14px] h-[14px] mt-[2px] text-analytics-gray dark:text-analytics-gray-dk" />
          <p className="text-md text-heading-text dark:text-heading-text-dk">
            Amount: {formatDealCurrency(deal.total_amount, deal.currency)}
          </p>
        </div>

        {deal.contract_end && (
          <div className="flex items-center gap-[6px]">
            <CalendarIcon className="shrink-0 w-[14px] h-[14px] mt-[2px] text-analytics-gray dark:text-analytics-gray-dk" />
            <p className="text-md text-heading-text dark:text-heading-text-dk">
              Close date:{" "}
              {getDateTimeByFormat(new Date(deal?.contract_end))?.date}
            </p>
          </div>
        )}

        {deal.contract_start && (
          <div className="flex items-center gap-[6px]">
            <CalendarIcon className="shrink-0 w-[14px] h-[14px] mt-[2px] text-analytics-gray dark:text-analytics-gray-dk" />
            <p className="text-md text-heading-text dark:text-heading-text-dk">
              Contract start:{" "}
              {getDateTimeByFormat(new Date(deal?.contract_start))?.date}
            </p>
          </div>
        )}

        {deal.property_name && (
          <div className="flex items-center gap-[6px]">
            <PropertyManagementIcon className="shrink-0 w-[14px] h-[14px] mt-[2px] text-analytics-gray dark:text-analytics-gray-dk" />
            <p className="text-md text-heading-text dark:text-heading-text-dk">
              Property: {deal.property_name}
            </p>
          </div>
        )}
      </div>

      <div
        className="border-t border-border-gray dark:border-border-gray-dk pt-[6px]"
        onClick={(e) => e.stopPropagation()}
      >
        <AgentAssignmentDropdown
          selectedAgentIds={deal.assignee ? [deal?.assignee?.id || ""] : []}
          onApply={async (ids) => {
            if (onAssignAgent) await onAssignAgent(deal.contract_id || "", ids);
          }}
          maxAgents={1}
          title="ASSIGN OWNER"
          subHeading={DEAL_LABELS.assignOwnerSubHeading}
          hideRole
          triggerMaxWidth="max-w-[276px]"
          triggerText={DEAL_LABELS.addOwner}
          triggerClassName="!border-none !border-0"
          onToggleScroll={handleScrollToggle}
          defaultSelectedAgents={defaultSelectedAgents}
        />
      </div>
    </div>
  );
};

export default DealKanbanCard;
